import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import { TrendingUp } from 'lucide-react'
import { cn } from '@/lib/utils'
import { PISA_DATA, PISA_SOURCES } from '@/constants/leesritme'

const SCALE_MAX = 45
const TICKS = [0, 10, 20, 30, 40]
const BAR_COLORS = ['#f4c7b3', '#eea283', '#e57d58', '#b54724']

export const PisaChart: FC = (): ReactNode => {
    const [active, setActive] = useState<number | null>(null)

    return (
        <figure className='rounded-[1.75rem] border border-ink/10 bg-card p-6 shadow-soft sm:p-8'>
            <figcaption className='flex flex-wrap items-start justify-between gap-4'>
                <span className='block max-w-sm'>
                    <span className='block font-heading text-xl leading-snug font-semibold text-ink'>
                        15-jarigen die onder niveau 2 lezen
                    </span>

                    <span className='mt-1 block text-sm text-muted-foreground'>
                        Aandeel in Nederland volgens PISA
                    </span>
                </span>

                <span className='inline-flex items-center gap-1.5 rounded-full bg-coral-soft px-3 py-1.5 text-sm font-bold text-coral-deep'>
                    <TrendingUp className='size-4' aria-hidden='true' />
                    In tien jaar meer dan verdubbeld
                </span>
            </figcaption>

            <div className='relative mt-8 h-64' aria-hidden='true'>
                {TICKS.map(tick => (
                    <div
                        key={tick}
                        className={cn('absolute inset-x-0 border-t', tick === 0 ? 'border-ink/25' : 'border-dashed border-ink/10')}
                        style={{ bottom: `${(tick / SCALE_MAX) * 100}%` }}
                    >
                        <span className='absolute -top-2.5 left-0 text-xs text-muted-foreground tabular-nums'>
                            {tick}%
                        </span>
                    </div>
                ))}

                <div className='absolute inset-y-0 right-0 left-10 grid grid-cols-4 gap-3 sm:gap-6'>
                    {PISA_DATA.map((item, index) => {
                        const height = (item.value / SCALE_MAX) * 100
                        const isActive = active === index

                        return (
                            <div
                                key={item.year}
                                className='relative h-full cursor-default'
                                onMouseEnter={() => setActive(index)}
                                onMouseLeave={() => setActive(null)}
                            >
                                <div
                                    className={cn('absolute inset-x-[14%] bottom-0 rounded-t-[6px] transition-[filter,transform] duration-200', isActive && 'brightness-95')}
                                    style={{ height: `${height}%`, backgroundColor: BAR_COLORS[index] }}
                                />

                                <span
                                    className='absolute inset-x-0 text-center font-heading text-lg font-semibold text-ink tabular-nums sm:text-xl'
                                    style={{ bottom: `calc(${height}% + 0.4rem)` }}
                                >
                                    {item.value}%
                                </span>

                                {isActive && (
                                    <span
                                        className='absolute left-1/2 z-10 w-44 -translate-x-1/2 rounded-xl bg-ink px-3 py-2 text-center text-xs leading-snug text-paper shadow-lift'
                                        style={{ bottom: `calc(${height}% + 2.6rem)` }}
                                    >
                                        In {item.year} las {item.value}% van de 15-jarigen onder niveau 2
                                    </span>
                                )}
                            </div>
                        )
                    })}
                </div>
            </div>

            <div className='mt-3 ml-10 grid grid-cols-4 gap-3 text-center text-sm font-semibold text-muted-foreground tabular-nums sm:gap-6' aria-hidden='true'>
                {PISA_DATA.map(item => (
                    <span key={item.year}>
                        {item.year}
                    </span>
                ))}
            </div>

            <table className='sr-only'>
                <caption>
                    Aandeel 15-jarigen in Nederland dat onder niveau 2 leest, volgens PISA
                </caption>

                <thead>
                    <tr>
                        <th scope='col'>Jaar</th>
                        <th scope='col'>Aandeel</th>
                    </tr>
                </thead>

                <tbody>
                    {PISA_DATA.map(item => (
                        <tr key={item.year}>
                            <th scope='row'>{item.year}</th>
                            <td>{item.value}%</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <p className='mt-6 text-xs text-muted-foreground'>
                Bron: PISA, via{' '}
                {PISA_SOURCES.map((source, index) => (
                    <span key={source.href}>
                        <a href={source.href} target='_blank' rel='noopener noreferrer' className='underline decoration-ink/20 underline-offset-2 hover:text-ink hover:decoration-ink'>
                            {source.label}
                        </a>
                        {index < PISA_SOURCES.length - 1 ? ' en ' : ''}
                    </span>
                ))}
            </p>
        </figure>
    )
}
